import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";

import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import PauseIcon from "@material-ui/icons/PauseCircleOutline";
import PlayIcon from "@material-ui/icons/PlayCircleOutline";
import CancelIcon from "@material-ui/icons/HighlightOff";
import AutorenewIcon from "@material-ui/icons/Autorenew";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import StatusChip from "../../components/StatusChip";
import ConfirmationModal from "../../components/ConfirmationModal";
import SubscriptionModal from "../../components/SubscriptionModal";
import useSocketSubscription from "../../hooks/useSocketSubscription";
import { displayClientName } from "../../utils/clientName";
import { semantic } from "../../theme/tokens";

export const PERIOD_LABEL = {
  monthly: "Mensal",
  quarterly: "Trimestral",
  semiannual: "Semestral",
  annual: "Anual",
};

const STATUS_META = {
  trialing: { label: "Em teste", tone: "info" },
  active: { label: "Ativa", tone: "success" },
  past_due: { label: "Em atraso", tone: "warning" },
  paused: { label: "Pausada", tone: "neutral" },
  canceled: { label: "Cancelada", tone: "danger" },
};

const useStyles = makeStyles((theme) => ({
  tile: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    height: "100%",
  },
  tileLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  tileValue: {
    fontSize: "1.6rem",
    fontWeight: 700,
    lineHeight: 1.2,
    fontVariantNumeric: "tabular-nums",
  },
  tileHint: { color: theme.palette.text.secondary },
  movement: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(3),
  },
  movRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  movLabel: { width: 110, fontSize: 13, fontWeight: 600 },
  movTrack: {
    flex: 1,
    height: 10,
    borderRadius: 5,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(148,163,184,0.14)"
        : theme.palette.grey[100],
    overflow: "hidden",
  },
  movBar: { height: 10, borderRadius: 5 },
  movValue: {
    width: 120,
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    fontSize: 13,
    fontWeight: 600,
  },
  section: { marginBottom: theme.spacing(3) },
  muted: { color: theme.palette.text.hint },
  secondary: { color: theme.palette.text.secondary },
  cell: { display: "flex", flexDirection: "column", gap: 1 },
  filter: { minWidth: 160 },
}));

const money = (v) =>
  Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const fmtDate = (d) =>
  d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString("pt-BR") : null;

const Subscriptions = () => {
  const classes = useStyles();
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [mrr, setMrr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [canceling, setCanceling] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [list, summary] = await Promise.all([
        api.get("/subscriptions", {
          params: { status: status || undefined, pageSize: 100 },
        }),
        api.get("/subscriptions/mrr"),
      ]);
      setRows(list.data.subscriptions || []);
      setCount(list.data.count || 0);
      setMrr(summary.data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useSocketSubscription("subscription", fetchAll);

  const handlePause = async (row) => {
    try {
      await api.post(`/subscriptions/${row.id}/pause`, {
        resume: row.status === "paused",
      });
      toast.success(row.status === "paused" ? "Assinatura retomada" : "Assinatura pausada");
      fetchAll();
    } catch (err) {
      toastError(err);
    }
  };

  const handleCancel = async () => {
    if (!canceling) return;
    try {
      await api.post(`/subscriptions/${canceling.id}/cancel`, {
        reason: canceling.reason || undefined,
      });
      toast.success("Assinatura cancelada");
      fetchAll();
    } catch (err) {
      toastError(err);
    }
    setCanceling(null);
  };

  const dash = <span className={classes.muted}>—</span>;

  const columns = [
    {
      key: "client",
      label: "Cliente",
      render: (r) => (
        <div className={classes.cell}>
          <Typography variant="subtitle2">{displayClientName(r.client)}</Typography>
          <Typography variant="caption" className={classes.secondary}>
            {r.plan?.name || "Sem plano"}
          </Typography>
        </div>
      ),
    },
    {
      key: "amount",
      label: "Valor",
      type: "numeric",
      render: (r) => (
        <div className={classes.cell}>
          <span>{money(r.amount)}</span>
          <Typography variant="caption" className={classes.secondary}>
            {PERIOD_LABEL[r.billingPeriod] || r.billingPeriod}
            {r.quantity > 1 ? ` · ${r.quantity}x` : ""}
          </Typography>
        </div>
      ),
    },
    {
      key: "mrr",
      label: "MRR",
      type: "numeric",
      render: (r) =>
        Number(r.mrr) > 0 ? (
          <strong>{money(r.mrr)}</strong>
        ) : (
          <span className={classes.muted}>{money(0)}</span>
        ),
    },
    {
      key: "nextBillingAt",
      label: "Próximo ciclo",
      render: (r) => fmtDate(r.nextBillingAt) || dash,
    },
    {
      key: "status",
      label: "Status",
      render: (r) => {
        const meta = STATUS_META[r.status] || { label: r.status, tone: "neutral" };
        return <StatusChip dot tone={meta.tone} label={meta.label} />;
      },
    },
    {
      key: "actions",
      label: "Ações",
      type: "actions",
      render: (r) => (
        <>
          {r.status !== "canceled" && (
            <>
              <Tooltip title={r.status === "paused" ? "Retomar" : "Pausar"}>
                <IconButton size="small" onClick={() => handlePause(r)}>
                  {r.status === "paused" ? (
                    <PlayIcon fontSize="small" />
                  ) : (
                    <PauseIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title="Editar valor ou periodicidade">
                <IconButton
                  size="small"
                  onClick={() => {
                    setEditingId(r.id);
                    setModalOpen(true);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cancelar">
                <IconButton size="small" onClick={() => setCanceling({ ...r, reason: "" })}>
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </>
      ),
    },
  ];

  const mov = mrr?.movement;
  // Escala comum aos quatro movimentos, senão barras de grandezas diferentes
  // sugerem proporções que não existem.
  const movMax = Math.max(
    1,
    mov?.new || 0,
    mov?.expansion || 0,
    mov?.contraction || 0,
    mov?.churn || 0
  );

  const movRows = [
    { label: "Novo", value: mov?.new || 0, color: semantic.success.main },
    { label: "Expansão", value: mov?.expansion || 0, color: semantic.info.main },
    { label: "Contração", value: mov?.contraction || 0, color: semantic.warning.main },
    { label: "Churn", value: mov?.churn || 0, color: semantic.danger.main },
  ];

  return (
    <MainContainer>
      <SubscriptionModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        subscriptionId={editingId}
        onSaved={fetchAll}
      />
      <ConfirmationModal
        title={canceling ? `Cancelar a assinatura de ${displayClientName(canceling.client)}?` : ""}
        open={!!canceling}
        onClose={() => setCanceling(null)}
        onConfirm={handleCancel}
      >
        O MRR de {canceling ? money(canceling.mrr) : ""} sai do total imediatamente e
        entra como churn no período.
      </ConfirmationModal>

      <PageHeader
        title="Assinaturas"
        count={count}
        subtitle="Receita recorrente. O MRR normaliza qualquer periodicidade para o valor mensal equivalente."
        actions={
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingId(null);
              setModalOpen(true);
            }}
          >
            Nova assinatura
          </Button>
        }
        filters={
          <TextField
            select
            label="Status"
            className={classes.filter}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <MenuItem value="">Todas</MenuItem>
            {Object.entries(STATUS_META).map(([value, meta]) => (
              <MenuItem key={value} value={value}>
                {meta.label}
              </MenuItem>
            ))}
          </TextField>
        }
      />

      <div style={{ paddingTop: 16 }}>
        <Grid container spacing={2} className={classes.section}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper variant="outlined" className={classes.tile}>
              <div className={classes.tileLabel}>MRR atual</div>
              <div className={classes.tileValue} style={{ color: semantic.success.main }}>
                {money(mrr?.currentMrr)}
              </div>
              <Typography variant="caption" className={classes.tileHint}>
                {mrr?.activeSubscriptions || 0} assinaturas ativas
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper variant="outlined" className={classes.tile}>
              <div className={classes.tileLabel}>ARR</div>
              <div className={classes.tileValue}>{money(mrr?.arr)}</div>
              <Typography variant="caption" className={classes.tileHint}>
                MRR × 12
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper variant="outlined" className={classes.tile}>
              <div className={classes.tileLabel}>Ticket médio</div>
              <div className={classes.tileValue}>{money(mrr?.averageTicket)}</div>
              <Typography variant="caption" className={classes.tileHint}>
                Por assinatura ativa
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper variant="outlined" className={classes.tile}>
              <div className={classes.tileLabel}>Churn de receita</div>
              <div
                className={classes.tileValue}
                style={mrr?.churnRate ? { color: semantic.danger.main } : undefined}
              >
                {mrr?.churnRate != null ? `${mrr.churnRate}%` : "—"}
              </div>
              <Typography variant="caption" className={classes.tileHint}>
                {mov?.churnedSubscriptions || 0}{" "}
                {mov?.churnedSubscriptions === 1 ? "cancelamento" : "cancelamentos"} no
                período
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Paper variant="outlined" className={classes.movement}>
          <Typography variant="h4" gutterBottom>
            Movimento de MRR nos últimos 30 dias
          </Typography>
          <Typography variant="caption" className={classes.secondary} style={{ display: "block", marginBottom: 16 }}>
            Os quatro aparecem separados de propósito: o saldo líquido sozinho
            esconde expansão que compensou cancelamento.
          </Typography>

          {movRows.map((m) => (
            <div key={m.label} className={classes.movRow}>
              <span className={classes.movLabel}>{m.label}</span>
              <div className={classes.movTrack}>
                <div
                  className={classes.movBar}
                  style={{ width: `${(m.value / movMax) * 100}%`, backgroundColor: m.color }}
                />
              </div>
              <span className={classes.movValue} style={{ color: m.color }}>
                {money(m.value)}
              </span>
            </div>
          ))}

          <div className={classes.movRow} style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid rgba(128,128,128,0.2)" }}>
            <span className={classes.movLabel}>Líquido</span>
            <div style={{ flex: 1 }} />
            <span
              className={classes.movValue}
              style={{
                color: (mov?.net || 0) >= 0 ? semantic.success.main : semantic.danger.main,
              }}
            >
              {(mov?.net || 0) >= 0 ? "+" : ""}
              {money(mov?.net)}
            </span>
          </div>
        </Paper>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        emptyIcon={<AutorenewIcon />}
        emptyTitle={status ? "Nenhuma assinatura com esse status" : "Nenhuma assinatura ainda"}
        emptyDescription={
          status
            ? "Troque o filtro para ver as demais."
            : "Cadastre um contrato recorrente para começar a acompanhar MRR, expansão e churn."
        }
        emptyAction={
          !status ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingId(null);
                setModalOpen(true);
              }}
            >
              Nova assinatura
            </Button>
          ) : null
        }
      />
    </MainContainer>
  );
};

export default Subscriptions;
